/**
 * API utility functions for Dynamic Properties
 */

import { instance } from "../../../lib/api";
import Constants from "../../../utils/CommonConstants";
import { parseJsonSafely } from "../../../utils/dynamicPropertiesUtils";
import { API_ENDPOINTS, META_KEY } from "../config";

/**
 * Handle API errors
 * @param {Error} err - Error object
 * @param {Function} dispatch - Redux dispatch function
 */
export const showError = (err, dispatch) => {
  dispatch({
    type: Constants.API_FAIL,
    errMsg: Constants.getErrorMessage(err),
  });
};

/**
 * Build URL with query parameters
 * @param {string} baseUrl - Base URL
 * @param {Object} params - Query parameters object
 * @returns {string} - URL with query string
 */
export const buildUrlWithParams = (baseUrl, params) => {
  const queryString = Object.keys(params)
    .map((key) => `${key}=${encodeURIComponent(params[key])}`)
    .join("&");
  return `${baseUrl}?${queryString}`;
};

/**
 * Fetch the meta key to get list of all property keys
 * @returns {Promise<Array<string>>} - Array of property keys
 */
export const fetchMetaKey = async () => {
  try {
    const url = buildUrlWithParams(API_ENDPOINTS.GET_BY_KEYS, {
      keys: META_KEY,
    });
    const response = await instance({
      url,
      method: "get",
    });
    const responseData = response.data?.data || [];
    if (responseData.length === 0) {
      return [];
    }

    const metaProperty = responseData[0];
    const parsedValue = parseJsonSafely(metaProperty.propValue);

    // Meta key stores an array of keys
    if (Array.isArray(parsedValue)) {
      return parsedValue;
    }

    // If it's an object with a keys array
    if (parsedValue && Array.isArray(parsedValue.keys)) {
      return parsedValue.keys;
    }

    return [];
  } catch (err) {
    // Meta key doesn't exist yet, return empty array
    return [];
  }
};

/**
 * Update the meta key with the list of keys
 * @param {Array<string>} keys - Array of keys to store
 * @returns {Promise} - API response promise
 */
export const updateMetaKey = async (keys) => {
  const url = buildUrlWithParams(API_ENDPOINTS.UPDATE, {
    key: META_KEY,
    value: JSON.stringify(keys),
  });

  return instance({
    url,
    method: "put",
  });
};

/**
 * Transform API response from propKey/propValue format to key/value format
 * @param {Array} responseData - Raw API response data
 * @returns {Array} - Transformed data array
 */
export const transformApiResponse = (responseData) => {
  return responseData.map((item) => ({
    key: item.propKey,
    value: item.propValue,
    id: item.id,
    createdBy: item.createdBy,
    updatedBy: item.updatedBy,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  }));
};
